import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Users, Home as HomeIcon, DollarSign, MessageCircle,
  CheckCircle, XCircle, BadgeCheck, Ban, Eye, Search, Flag, Trash2,
  AlertTriangle, Copy as CopyIcon, Mail, CalendarClock, MapPin, Briefcase,
  Send, TrendingUp, Building2, HandCoins, Loader2,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { formatCOP } from '../mock';
import { api, thumbUrl } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import EmailBackfillPanel from '../components/admin/EmailBackfillPanel';
import FollowupPanel from '../components/admin/FollowupPanel';
import GeocodingPanel from '../components/admin/GeocodingPanel';
import AgencyLeadsPanel from '../components/admin/AgencyLeadsPanel';
import CampaignsPanel from '../components/admin/CampaignsPanel';
import InvestmentBaselinesPanel from '../components/admin/InvestmentBaselinesPanel';
import PricingBaselinesPanel from '../components/admin/PricingBaselinesPanel';
import ConstructorProjectsPanel from '../components/admin/ConstructorProjectsPanel';
import SellerLeadsPanel from '../components/admin/SellerLeadsPanel';
import DistributionPanel from '../components/admin/DistributionPanel';
import DistributionPreviewModal from '../components/admin/DistributionPreviewModal';

const TABS = [
  { id: 'overview', label: 'Resumen', icon: Shield },
  { id: 'pending', label: 'Pendientes', icon: HomeIcon },
  { id: 'all', label: 'Propiedades', icon: HomeIcon },
  { id: 'reports', label: 'Reportes', icon: Flag },
  { id: 'duplicates', label: 'Duplicados', icon: CopyIcon },
  { id: 'users', label: 'Usuarios', icon: Users },
  { id: 'emails', label: 'Emails', icon: Mail },
  { id: 'campaigns', label: 'Campañas', icon: Send },
  { id: 'followups', label: 'Seguimientos', icon: CalendarClock },
  { id: 'geocoding', label: 'Geocodificación', icon: MapPin },
  { id: 'leads', label: 'Leads Inmobiliarias', icon: Briefcase },
  { id: 'seller-leads', label: 'Leads de Vendedores', icon: HandCoins },
  { id: 'investment', label: 'Baselines Inversión', icon: TrendingUp },
  { id: 'pricing', label: 'Baselines Precios', icon: DollarSign },
  { id: 'constructoras', label: 'Constructoras', icon: Building2 },
  { id: 'distribution', label: 'Distribution Center', icon: Send },
];

const REASON_LABEL = {
  'precio-falso': 'Precio falso',
  'fotos-falsas': 'Fotos falsas',
  'duplicada': 'Duplicada',
  'fraude': 'Fraude',
  'datos-incorrectos': 'Datos incorrectos',
  'otro': 'Otro',
};

export default function AdminDashboard() {
  const { toast } = useToast();
  const [tab, setTab] = useState('overview');
  const [previewId, setPreviewId] = useState(null);
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [allProps, setAllProps] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [reports, setReports] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters for "all properties"
  const [search, setSearch] = useState('');
  const [filterVerified, setFilterVerified] = useState(''); // '', 'true', 'false'
  const [filterReports, setFilterReports] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, p, a, u, r, d] = await Promise.all([
        api.adminStats(),
        api.adminProperties({ status: 'pendiente' }),
        api.adminProperties({
          status: 'activa',
          q: search || undefined,
          verified: filterVerified === '' ? undefined : filterVerified === 'true',
          has_reports: filterReports || undefined,
        }),
        api.adminUsers(),
        api.adminReports('pending'),
        api.adminDuplicates(),
      ]);
      setStats(s);
      setPending(p.items);
      setAllProps(a.items);
      setUsersList(u.items);
      setReports(r.items);
      setDuplicates(d.items);
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo cargar', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (tab === 'all') {
      const t = setTimeout(reload, 250);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [search, filterVerified, filterReports]);

  const approveProp = async (id) => { await api.adminApprove(id); toast({ title: 'Aprobada' }); await reload(); };
  const rejectProp = async (id) => {
    const reason = prompt('Razón del rechazo:');
    if (reason !== null) {
      await api.adminReject(id, reason);
      toast({ title: 'Rechazada' });
      await reload();
    }
  };
  const verifyProp = async (id) => { await api.adminVerifyProperty(id); toast({ title: 'Verificada' }); await reload(); };
  const unverifyProp = async (id) => { await api.adminUnverifyProperty(id); toast({ title: 'Verificación retirada' }); await reload(); };
  const deleteProp = async (id) => {
    if (confirm('¿Eliminar esta propiedad? Esta acción no se puede deshacer.')) {
      await api.adminDeleteProperty(id);
      toast({ title: 'Eliminada' });
      await reload();
    }
  };
  const banUser = async (id) => { if (confirm('¿Suspender este usuario?')) { await api.adminBan(id); await reload(); } };
  const unbanUser = async (id) => { await api.adminUnban(id); await reload(); };
  const verifyUser = async (id) => { await api.adminVerify(id); toast({ title: 'Usuario verificado' }); await reload(); };
  const unverifyUser = async (id) => { await api.adminUnverify(id); await reload(); };
  const resolveReport = async (id) => { await api.adminResolveReport(id); toast({ title: 'Reporte resuelto' }); await reload(); };
  const dismissReport = async (id) => { await api.adminDismissReport(id); toast({ title: 'Reporte descartado' }); await reload(); };
  const distributeProp = (id) => setPreviewId(id);

  return (
    <div className="min-h-screen bg-habita-cream">
      <Header />
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-habita flex items-center justify-center"><Shield className="w-5 h-5 text-white" /></div>
          <div className="flex-1">
            <h1 className="font-serif text-3xl lg:text-4xl font-bold text-habita-dark">Panel de administración</h1>
            <p className="text-sm text-gray-500">Modera propiedades, reportes y usuarios.</p>
          </div>
          <Link
            to="/admin/dashboard"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-habita text-white text-sm font-semibold hover:bg-habita-dark transition-colors shadow-sm"
            data-testid="link-executive-dashboard"
          >
            Dashboard Ejecutivo →
          </Link>
          <Link
            to="/admin/advisor/dashboard"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-habita text-white text-sm font-semibold hover:bg-habita-dark transition-colors ml-2"
            data-testid="link-advisor-dashboard"
          >
            Advisor · Producto →
          </Link>
          <Link
            to="/admin/advisor"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-emerald-600 text-habita-dark text-sm font-semibold hover:bg-habita hover:text-white transition-colors ml-2"
            data-testid="link-advisor-inspector"
          >
            Advisor Inspector →
          </Link>
        </div>

        <div className="mt-8 flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1 w-fit overflow-x-auto no-scrollbar max-w-full">
          {TABS.map(t => {
            const badge =
              t.id === 'pending' ? pending.length :
              t.id === 'reports' ? reports.length :
              t.id === 'duplicates' ? duplicates.length :
              0;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`shrink-0 px-4 md:px-5 h-10 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${tab === t.id ? 'bg-habita text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
                {badge > 0 && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-white/20' : 'bg-amber-500 text-white'}`}>{badge}</span>
                )}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl p-20 text-center mt-6 border border-gray-200">
            <div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="mt-6">
            {tab === 'overview' && stats && (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: Users, label: 'Usuarios totales', value: stats.users },
                  { icon: BadgeCheck, label: 'Agentes', value: stats.agents },
                  { icon: HomeIcon, label: 'Propiedades activas', value: stats.active_properties },
                  { icon: BadgeCheck, label: 'Verificadas', value: stats.verified_properties, accent: 'text-emerald-600' },
                  { icon: HomeIcon, label: 'Pendientes', value: stats.pending_properties, accent: 'text-amber-600' },
                  { icon: Flag, label: 'Con reportes', value: stats.reported_properties, accent: 'text-rose-600' },
                  { icon: AlertTriangle, label: 'Reportes pendientes', value: stats.pending_reports, accent: 'text-rose-600' },
                  { icon: DollarSign, label: 'Suscripciones activas', value: stats.active_subscriptions },
                  { icon: MessageCircle, label: 'Mensajes', value: stats.total_messages },
                  { icon: DollarSign, label: 'Ingresos totales', value: formatCOP(stats.revenue_total) },
                ].map((s, i) => (
                  <div key={i} className="bg-white rounded-2xl p-5 border border-gray-200">
                    <div className={`w-10 h-10 rounded-xl bg-habita-mint flex items-center justify-center ${s.accent || 'text-habita'}`}>
                      <s.icon className="w-5 h-5" />
                    </div>
                    <p className={`font-serif text-3xl font-bold mt-4 ${s.accent || 'text-habita-dark'}`}>{s.value}</p>
                    <p className="text-xs text-gray-500 mt-1 uppercase tracking-widest">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* PENDING TAB */}
            {tab === 'pending' && (
              <PropList
                items={pending}
                emptyMsg="No hay propiedades en espera de aprobación. ¡Todo al día!"
                onApprove={approveProp}
                onReject={rejectProp}
                onVerify={verifyProp}
                onDelete={deleteProp}
                onDistribute={distributeProp}
                showApprove
              />
            )}

            {/* ALL TAB */}
            {tab === 'all' && (
              <>
                {/* Search + filters */}
                <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4 flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full flex-1 min-w-[220px]">
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Buscar por título, dirección o barrio..."
                      className="bg-transparent outline-none text-sm w-full"
                      data-testid="admin-search-input"
                    />
                  </div>
                  <select
                    value={filterVerified}
                    onChange={(e) => setFilterVerified(e.target.value)}
                    className="bg-gray-100 rounded-full px-4 h-10 text-sm font-medium outline-none cursor-pointer"
                    data-testid="admin-filter-verified"
                  >
                    <option value="">Todas (verificación)</option>
                    <option value="true">Solo verificadas</option>
                    <option value="false">No verificadas</option>
                  </select>
                  <label className="flex items-center gap-2 px-4 h-10 rounded-full bg-gray-100 cursor-pointer text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={filterReports}
                      onChange={(e) => setFilterReports(e.target.checked)}
                      className="accent-[#00824D]"
                    />
                    Con reportes
                  </label>
                </div>

                <PropList
                  items={allProps}
                  emptyMsg="No se encontraron propiedades con esos filtros."
                  onVerify={verifyProp}
                  onUnverify={unverifyProp}
                  onReject={rejectProp}
                  onDelete={deleteProp}
                  onDistribute={distributeProp}
                />
              </>
            )}

            {/* REPORTS TAB */}
            {tab === 'reports' && (
              <div className="space-y-3">
                {reports.length === 0 ? (
                  <div className="bg-white rounded-2xl p-16 text-center border border-gray-200">
                    <Flag className="w-10 h-10 text-gray-300 mx-auto" />
                    <p className="text-gray-500 mt-3">No hay reportes pendientes. ¡Excelente!</p>
                  </div>
                ) : reports.map(r => (
                  <div key={r.id} className="bg-white rounded-2xl p-4 border border-gray-200 flex flex-col md:flex-row gap-4" data-testid={`report-row-${r.id}`}>
                    {r.property?.images?.[0] && (
                      <img src={thumbUrl(r.property.images[0], { w: 300, h: 200 })} alt="" className="w-full md:w-32 h-24 md:h-24 object-cover rounded-xl" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-rose-100 text-rose-700">{REASON_LABEL[r.reason] || r.reason}</span>
                          <h3 className="font-serif text-lg font-bold text-habita-dark mt-2">{r.property?.title || 'Propiedad eliminada'}</h3>
                          <p className="text-xs text-gray-500">{r.property?.address}{r.property?.city ? ` · ${r.property.city}` : ''}</p>
                        </div>
                        <span className="text-[10px] text-gray-400 uppercase tracking-widest">
                          {new Date(r.created_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                      {r.details && <p className="text-sm text-gray-700 mt-2 italic">"{r.details}"</p>}
                      <p className="text-xs text-gray-400 mt-2">
                        Reportado por: {r.reporter_email || 'usuario registrado'} ·
                        {' '}Total reportes: <strong>{r.property?.report_count || 0}</strong>
                      </p>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {r.property?.id && (
                          <Link to={`/propiedad/${r.property_id}`} target="_blank" className="text-xs font-semibold px-3 h-8 rounded-full border border-gray-200 hover:border-habita flex items-center gap-1">
                            <Eye className="w-3 h-3" /> Ver propiedad
                          </Link>
                        )}
                        <button onClick={() => resolveReport(r.id)} className="text-xs font-semibold px-3 h-8 rounded-full bg-habita text-white hover:bg-habita-dark flex items-center gap-1" data-testid={`resolve-report-${r.id}`}>
                          <CheckCircle className="w-3 h-3" /> Resolver
                        </button>
                        <button onClick={() => dismissReport(r.id)} className="text-xs font-semibold px-3 h-8 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-1">
                          <XCircle className="w-3 h-3" /> Descartar
                        </button>
                        {r.property?.id && (
                          <button onClick={() => deleteProp(r.property_id)} className="text-xs font-semibold px-3 h-8 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center gap-1">
                            <Trash2 className="w-3 h-3" /> Eliminar publicación
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* DUPLICATES TAB */}
            {tab === 'duplicates' && (
              <div className="space-y-3">
                {duplicates.length === 0 ? (
                  <div className="bg-white rounded-2xl p-16 text-center border border-gray-200">
                    <CopyIcon className="w-10 h-10 text-gray-300 mx-auto" />
                    <p className="text-gray-500 mt-3">No se detectaron publicaciones duplicadas.</p>
                  </div>
                ) : duplicates.map((g, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-5 border border-amber-200" data-testid={`duplicate-group-${idx}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-habita-dark">{g.count} publicaciones en la misma dirección</p>
                        <p className="text-xs text-gray-500">{g.address}</p>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {g.property_ids.map((pid, i) => (
                        <li key={pid} className="flex items-center justify-between gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50">
                          <Link to={`/propiedad/${pid}`} target="_blank" className="text-sm text-gray-700 hover:text-habita truncate flex-1">
                            {g.titles[i]}
                          </Link>
                          <button onClick={() => deleteProp(pid)} className="text-xs font-semibold px-3 h-8 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center gap-1 shrink-0">
                            <Trash2 className="w-3 h-3" /> Eliminar
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* USERS TAB */}
            {tab === 'users' && (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm min-w-[800px]">
                  <thead className="bg-habita-cream">
                    <tr>
                      <th className="text-left p-4 font-semibold text-habita-dark">Usuario</th>
                      <th className="text-left p-4 font-semibold text-habita-dark">Rol</th>
                      <th className="text-left p-4 font-semibold text-habita-dark">Plan</th>
                      <th className="text-left p-4 font-semibold text-habita-dark">Estado</th>
                      <th className="text-right p-4 font-semibold text-habita-dark">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.map(u => (
                      <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            {u.avatar
                              ? <img src={u.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                              : <div className="w-8 h-8 rounded-full bg-habita-mint flex items-center justify-center text-habita text-xs font-bold">{u.name?.[0]}</div>}
                            <div>
                              <p className="font-medium text-habita-dark">{u.name} {u.verified && <BadgeCheck className="w-3.5 h-3.5 inline text-habita" />}</p>
                              <p className="text-xs text-gray-500">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4"><span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-gray-100 text-gray-700">{u.role}</span></td>
                        <td className="p-4 text-gray-700">{u.plan_id}</td>
                        <td className="p-4">
                          {u.banned
                            ? <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-rose-100 text-rose-700">Suspendido</span>
                            : <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-habita-mint text-habita">Activo</span>}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            {u.verified
                              ? <button onClick={() => unverifyUser(u.id)} className="text-xs font-semibold px-3 h-7 rounded-full border border-gray-200 hover:border-gray-400">Quitar ✓</button>
                              : <button onClick={() => verifyUser(u.id)} className="text-xs font-semibold px-3 h-7 rounded-full border border-gray-200 hover:border-habita flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Verificar</button>}
                            {u.banned
                              ? <button onClick={() => unbanUser(u.id)} className="text-xs font-semibold px-3 h-7 rounded-full border border-habita text-habita hover:bg-habita-mint">Reactivar</button>
                              : <button onClick={() => banUser(u.id)} className="text-xs font-semibold px-3 h-7 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center gap-1"><Ban className="w-3 h-3" /> Suspender</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'emails' && <EmailBackfillPanel />}

            {tab === 'campaigns' && <CampaignsPanel />}

            {tab === 'followups' && <FollowupPanel />}

            {tab === 'geocoding' && <GeocodingPanel />}

            {tab === 'leads' && <AgencyLeadsPanel />}
            {tab === 'seller-leads' && <SellerLeadsPanel />}
            {tab === 'distribution' && <DistributionPanel />}

            {tab === 'investment' && <InvestmentBaselinesPanel />}
            {tab === 'pricing' && <PricingBaselinesPanel />}
            {tab === 'constructoras' && <ConstructorProjectsPanel />}
          </div>
        )}
      </div>
      <Footer />
      {previewId && (
        <DistributionPreviewModal propertyId={previewId} onClose={() => setPreviewId(null)} />
      )}
    </div>
  );
}

function PropList({ items, emptyMsg, showApprove, onApprove, onReject, onVerify, onUnverify, onDelete, onDistribute }) {
  const [busyId, setBusyId] = useState(null);
  const handleDistribute = async (id) => {
    setBusyId(id);
    try { await onDistribute(id); } finally { setBusyId(null); }
  };
  if (!items || items.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-16 text-center border border-gray-200">
        <p className="text-gray-500">{emptyMsg}</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {items.map(p => (
        <div key={p.id} className="bg-white rounded-2xl p-4 border border-gray-200 flex flex-col md:flex-row gap-4" data-testid={`admin-prop-${p.id}`}>
          <img src={thumbUrl(p.images?.[0] || '', { w: 400, h: 280 })} alt="" className="w-full md:w-40 h-28 object-cover rounded-xl bg-gray-100" />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${p.status === 'activa' ? 'bg-habita-mint text-habita' : p.status === 'pendiente' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>{p.status}</span>
              {p.verified && (
                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 flex items-center gap-1">
                  <BadgeCheck className="w-3 h-3" /> Verificada
                </span>
              )}
              {p.report_count > 0 && (
                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-rose-100 text-rose-700 flex items-center gap-1">
                  <Flag className="w-3 h-3" /> {p.report_count}
                </span>
              )}
            </div>
            <h3 className="font-serif text-lg font-bold text-habita-dark mt-2">{p.title}</h3>
            <p className="text-xs text-gray-500">{p.address} · {p.city}</p>
            <p className="text-sm font-semibold text-habita mt-1">{formatCOP(p.price)}{p.operation === 'arriendo' && <span className="text-xs text-gray-500"> /mes</span>}</p>
            {p.owner && (
              <p className="text-xs text-gray-400 mt-1">
                Owner: {p.owner.name} ({p.owner.email}) · {p.owner.role}{p.owner.verified && ' ✓'}
              </p>
            )}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Link to={`/propiedad/${p.id}`} target="_blank" className="text-xs font-semibold px-3 h-8 rounded-full border border-gray-200 hover:border-habita flex items-center gap-1">
                <Eye className="w-3 h-3" /> Ver
              </Link>
              {onDistribute && (
                <button onClick={() => handleDistribute(p.id)} disabled={busyId === p.id} className="text-xs font-semibold px-3 h-8 rounded-full border border-habita text-habita hover:bg-habita/5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1" data-testid={`distribute-${p.id}`} title="Publicar en redes sociales (Distribution Center)">
                  {busyId === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {busyId === p.id ? 'Distribuyendo…' : 'Distribuir ahora'}
                </button>
              )}
              {showApprove && onApprove && (
                <button onClick={() => onApprove(p.id)} className="text-xs font-semibold px-3 h-8 rounded-full bg-habita text-white hover:bg-habita-dark flex items-center gap-1" data-testid={`approve-${p.id}`}>
                  <CheckCircle className="w-3 h-3" /> Aprobar
                </button>
              )}
              {!p.verified && onVerify && (
                <button onClick={() => onVerify(p.id)} className="text-xs font-semibold px-3 h-8 rounded-full border border-emerald-200 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1" data-testid={`verify-${p.id}`}>
                  <BadgeCheck className="w-3 h-3" /> Verificar
                </button>
              )}
              {p.verified && onUnverify && (
                <button onClick={() => onUnverify(p.id)} className="text-xs font-semibold px-3 h-8 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-1">
                  Quitar ✓
                </button>
              )}
              {onReject && (
                <button onClick={() => onReject(p.id)} className="text-xs font-semibold px-3 h-8 rounded-full border border-amber-200 text-amber-700 hover:bg-amber-50 flex items-center gap-1">
                  <XCircle className="w-3 h-3" /> Rechazar
                </button>
              )}
              {onDelete && (
                <button onClick={() => onDelete(p.id)} className="text-xs font-semibold px-3 h-8 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center gap-1" data-testid={`delete-${p.id}`}>
                  <Trash2 className="w-3 h-3" /> Eliminar
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
